import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as TargetComplianceService from 'react/services/TargetComplianceService';
import {
  getFullDemographicsDataWithGenderAgeBySegment,
  createAlwaysOnAudience,
} from 'react/services/AudienceInsightsService';
import SingleSelectToggle from 'react/components/common/SingleSelectToggle/SingleSelectToggle';
import ConfirmationModal from 'react/components/common/Modal/ConfirmationModal/ConfirmationModal';
import ComplianceModal from 'react/components/apps/channelInsights/ComplianceModal/ComplianceModal';
import SegmentNameModal from 'react/components/apps/audience/AudienceBuilder/Modals/SegmentNameModal/SegmentNameModal';
import CategoryActivationModal from 'react/components/apps/audience/AudienceBuilder/Modals/CategoryActivationModal/CategoryActivationModal';
import TopicSelector from './TopicSelectorWidget/TopicSelectorWidget';
import SelectableBarChart from './SelectableBarChart/SelectableBarChart';
import QuickTable from './QuickTable/QuickTable';
import CategoriesTable from './CategoriesTable/CategoriesTable';
import {
  isCategoriesEnabled,
  isPurchaseAffinitiesEnabled,
  isCategoryTargetingVisible,
} from 'data/audience-segment-builder-helper';

// Local imports
import {
  VIEW_TYPES_NAMES,
  ALL_INTERESTS,
  ALL_CATEGORIES,
  VIEW_TYPES,
  SORT_TYPES,
  DEFAULT_SORT,
  TOO_WIDE_MSG,
  TOO_NARROW_MSG,
} from './constants';
import {
  useTopicsData,
  useInterestsData,
  useTestedInterests,
  useAudienceActivation,
  useAudienceUI,
  useToolbarLeftPosition,
  useInterestTesting,
  useComplianceModal,
  useAudienceConfig,
  useEventHandlers,
} from './hooks';
import { InterestInputBar, AudienceInsightsBottomBar } from './components';

import './AudienceInsightsWidget.scss';

const AudienceInsightsWidget = ({
  handleActivatingAudience,
  handleActivatingTarget,
  navToPrevTab,
  navToAudienceBuilder,
  exportControl,
  selectedTab,
  tabsNames,
  audienceChannel: propsAudienceChannel,
  segment: propsSegment,
  context,
  abiPermissions,
  audienceTableStructure,
  infraUtil,
  termsMold,
  keywordSuggester,
  defineInterestModal,
  notificator,
  dspService,
  ssoService,
  user,
  targetsCommon,
  targets = [],
  $state,
  $scope,
}) => {
  const isCategoriesLikeTab = [tabsNames?.categories, tabsNames?.purchaseAffinities].includes(selectedTab?.value);
  const categoryAffinityType = selectedTab?.value === tabsNames?.purchaseAffinities ? 'purchase' : 'audience';

  // Configuration hook - derives all config values from props/context
  const { audienceChannel, segment, includeSearches, interestTypes, interestTypesMap, targetRatioObj } =
    useAudienceConfig({
      propsAudienceChannel,
      propsSegment,
      context,
      abiPermissions,
    });

  // Local state
  const [navBackModal, setNavBackModal] = useState({ isOpen: false });
  const [sortType, setSortType] = useState(DEFAULT_SORT);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Category activation modal states
  const [isSegmentNameModalOpen, setIsSegmentNameModalOpen] = useState(false);
  const [isCategoryActivationModalOpen, setIsCategoryActivationModalOpen] = useState(false);
  const [pendingSegmentName, setPendingSegmentName] = useState('');
  const [pendingCategory, setPendingCategory] = useState(null);
  const [isCategoryActivationProcessing, setIsCategoryActivationProcessing] = useState(false);

  // Category selection handler
  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory((prev) => (prev?.id === category?.id ? null : category));
  }, []);

  // Category activation handlers
  const handleActivateSelectedCategory = useCallback((category) => {
    setPendingCategory(category);
    setIsSegmentNameModalOpen(true);
  }, []);

  const handleSegmentNameSubmit = useCallback((name) => {
    setPendingSegmentName(name);
    setIsSegmentNameModalOpen(false);
    // Small delay to ensure first modal is fully closed before opening second
    setTimeout(() => {
      setIsCategoryActivationModalOpen(true);
    }, 100);
  }, []);

  const handleSegmentNameCancel = useCallback(() => {
    setIsSegmentNameModalOpen(false);
    setPendingCategory(null);
    setPendingSegmentName('');
  }, []);

  const handleCategoryActivationSubmit = useCallback(
    async ({ market, advertiser, syndicateTo }) => {
      if (!pendingCategory || !pendingSegmentName) return;

      setIsCategoryActivationProcessing(true);
      try {
        const { accessToken } = await ssoService.getAccessToken();
        // Use the selected category ID for activation
        // Note: For category targeting, we create an always-on segment with the selected category
        await createAlwaysOnAudience(
          segment,
          null, // segmentId - will be generated
          pendingSegmentName,
          audienceChannel,
          null, // dataContractId - not needed for category targeting
          null, // dataContractText - not needed for category targeting
          pendingCategory.id,
          2, // Default amplify threshold
          accessToken
        );

        notificator.success({
          body: `<p>Your segment "${pendingSegmentName}" was successfully activated and will be available for targeting within 24 hours.</p>`,
        });

        // Clear states after successful activation
        setSelectedCategory(null);
        setPendingCategory(null);
        setPendingSegmentName('');
        setIsCategoryActivationModalOpen(false);
      } catch (error) {
        notificator.error({
          body: `Something went wrong... We couldn't activate the segment "${pendingSegmentName}". Please try again later.`,
        });
      } finally {
        setIsCategoryActivationProcessing(false);
      }
    },
    [pendingCategory, pendingSegmentName, segment, audienceChannel, ssoService, notificator]
  );

  const handleCategoryActivationCancel = useCallback(() => {
    setIsCategoryActivationModalOpen(false);
    setPendingCategory(null);
    setPendingSegmentName('');
  }, []);

  // Helper functions
  const openNavBackModal = useCallback((msg, modalTitle) => {
    setNavBackModal({
      isOpen: true,
      title: modalTitle,
      message: msg,
    });
  }, []);

  const expelUserIfMissingData = useCallback(
    (reason) => {
      const message = reason?.message || reason;
      switch (message) {
        case 'tooNarrow':
          openNavBackModal(TOO_NARROW_MSG, 'Audience is too narrow');
          break;
        case 'tooWide':
          openNavBackModal(TOO_WIDE_MSG, 'Audience is too wide');
          break;
        default:
          break;
      }
      return Promise.reject(reason);
    },
    [openNavBackModal]
  );

  // Custom hooks
  const { sortedTopicsData, topic, setTopic, topicsPromise } = useTopicsData({
    segment,
    audienceChannel,
    targetRatioObj,
    sortType,
    selectedTab,
    tabsNames,
    infraUtil,
    expelUserIfMissingData,
  });

  const {
    interests,
    fullInterests,
    setInterests,
    resetInterests,
    noDataMsg,
    getPromiseOfInterestsByTypeAndTopic,
    publishAllInterestsToView,
  } = useInterestsData({
    segment,
    audienceChannel,
    targetRatioObj,
    sortType,
    infraUtil,
    interestTypesMap,
    expelUserIfMissingData,
    categoryAffinityType,
  });

  const {
    newlyAddedInterestsToTest,
    setNewlyAddedInterestsToTest,
    getPromiseOfTestedInterestsByType,
    deleteTestedInterest,
    saveTestedInterestsToContext,
    processNewTestedInterests,
  } = useTestedInterests({
    segment,
    audienceChannel,
    targetRatioObj,
    context,
    notificator,
  });

  const activation = useAudienceActivation({
    context,
    audienceChannel,
    abiPermissions,
    user,
    dspService,
    segment,
  });

  // UI State hook - owns viewType, title, interestType, showTargets, queryData
  const {
    viewType,
    title,
    setTitle,
    interestType,
    showTargets,
    queryData,
    setQueryData,
    doPublishAllInterestsToView,
    handleInterestTypeChange,
    handleViewTypeChange,
    handleSortTypeChange,
  } = useAudienceUI({
    selectedTab,
    tabsNames,
    interestTypes,
    audienceChannel,
    abiPermissions,
    interests,
    setInterests,
    resetInterests,
    topic,
    setTopic,
    targetsCommon,
    targets,
    $state,
    $scope,
    publishAllInterestsToView,
    getPromiseOfTestedInterestsByType,
    sortType,
    setSortType,
  });
  const isCategoriesMode = isCategoriesLikeTab || interestType === 'categories';
  const currentViewType = isCategoriesMode ? VIEW_TYPES_NAMES.table : viewType;

  // Interest testing hook - handles categories highlighting + newly added interests
  useInterestTesting({
    segment,
    interestType,
    interestTypes,
    interests,
    audienceChannel,
    context,
    setInterests,
    setTopic,
    newlyAddedInterestsToTest,
    setNewlyAddedInterestsToTest,
    saveTestedInterestsToContext,
    processNewTestedInterests,
    getPromiseOfInterestsByTypeAndTopic,
    doPublishAllInterestsToView,
  });

  // Compliance modal hook - owns compliance state + handlers
  const { openComplianceModal, handleCloseCompliance, handleSaveWithNonCompliant, handleSaveWithoutNonCompliant } =
    useComplianceModal({
      queryData,
      setQueryData,
      interestType,
      targetsCommon,
      $scope,
      $state,
    });

  // Cleanup on unmount
  useEffect(
    () => () => {
      TargetComplianceService.unloadTarget();
    },
    []
  );

  // Clear category selection when view type changes to bars or interest type changes
  useEffect(() => {
    if (viewType === VIEW_TYPES_NAMES.bars || interestType !== 'categories') {
      setSelectedCategory(null);
    }
  }, [viewType, interestType]);

  // Event handlers hook
  const { handleTopicChange, handleDeleteTestedInterest, handleDefineInterest, handleOpenTargets } = useEventHandlers({
    interestType,
    audienceChannel,
    segment,
    context,
    topic,
    setTopic,
    setTitle,
    queryData,
    targets,
    targetsCommon,
    defineInterestModal,
    setNewlyAddedInterestsToTest,
    deleteTestedInterest,
    setInterests,
    doPublishAllInterestsToView,
  });

  // Export functionality
  const getAudienceInsightsExportDependencies = useCallback(() => {
    const allInterestsByTypeAndTopic = (type, topicId, overrideAffinity) =>
      getPromiseOfInterestsByTypeAndTopic(type, topicId, overrideAffinity).then((data) => data.forTable || []);

    const allPhrasesPromise = allInterestsByTypeAndTopic('phrases', ALL_INTERESTS);
    const allWebsitesPromise = allInterestsByTypeAndTopic('websites', ALL_INTERESTS);
    const allSearchesPromise = includeSearches
      ? allInterestsByTypeAndTopic('searches', ALL_INTERESTS)
      : Promise.resolve();
    const demographicsPromise = getFullDemographicsDataWithGenderAgeBySegment(segment, { channel: audienceChannel });

    // Fetch each affinity type independently so the export always contains
    // both Audience Affinities and Purchase Affinities when the user is entitled.
    const audienceCategoriesPromise = isCategoriesEnabled(segment, audienceChannel, abiPermissions)
      ? allInterestsByTypeAndTopic('categories', ALL_INTERESTS, 'audience')
      : Promise.resolve();
    const purchaseAffinitiesPromise = isPurchaseAffinitiesEnabled(segment, audienceChannel, abiPermissions)
      ? allInterestsByTypeAndTopic('categories', ALL_INTERESTS, 'purchase')
      : Promise.resolve();

    return Promise.all([
      topicsPromise,
      allWebsitesPromise,
      allPhrasesPromise,
      allSearchesPromise,
      audienceCategoriesPromise,
      purchaseAffinitiesPromise,
      demographicsPromise,
    ]);
  }, [segment, audienceChannel, abiPermissions, includeSearches, topicsPromise, getPromiseOfInterestsByTypeAndTopic]);

  useEffect(() => {
    if (exportControl) {
      exportControl.getAudienceInsightsExportDependencies = getAudienceInsightsExportDependencies;
    }
  }, [exportControl, getAudienceInsightsExportDependencies]);

  // Render table view
  const renderTable = () => {
    if (currentViewType !== VIEW_TYPES_NAMES.table) return null;

    if (interestType === 'categories') {
      return (
        <div className="table-container interests-table categories">
          <CategoriesTable
            data={fullInterests || []}
            hiddenColumnIds={categoryAffinityType === 'purchase' ? ['reach', 'id'] : []}
            loading={!interests}
            title={title}
            selectedCategory={selectedCategory}
            onSelectCategory={handleCategorySelect}
            isCategoryTargetingVisible={isCategoryTargetingVisible(abiPermissions)}
          />
        </div>
      );
    }

    if (!interests) return null;

    const tableStructure = audienceTableStructure?.tableStructure?.[audienceChannel] || audienceTableStructure || {};
    const columns = tableStructure.columns || [];
    columns[0].title = interestType;
    return (
      <div className="table-container interests-table">
        <QuickTable
          id={interestType}
          title={title}
          columns={columns}
          data={interests}
          loading={!interests}
          order={tableStructure.order || sortType}
          reverse={tableStructure.reverse !== undefined ? tableStructure.reverse : true}
          clickRowHandler={(row) => {
            if (queryData.clickRowHandler) {
              queryData.clickRowHandler(row);
            }
          }}
          className={interestType}
          disabledIds={{}}
          emphasisedIds={{}}
          selectedRow={queryData.selected && queryData.selected.length === 1 ? queryData.selected[0] : null}
        />
      </div>
    );
  };

  // Toolbar offset is measured after commit (the chart title mounts after this render) — see hook.
  const rootRef = useRef(null);
  const leftPosition = useToolbarLeftPosition(rootRef, title);

  // JSX
  return (
    <div
      ref={rootRef}
      className={classNames('audience-insights-widget main-content-page', {
        'show-audience-categories': isCategoriesLikeTab,
      })}
    >
      <div className="audience-insights-main-contents">
        {!isCategoriesLikeTab && (
          <>
            <div className="audience-insights-top-bar">
              <div className="title-wrapper">
                <div className="main-title">Audience Top Interests</div>
                <div className="sub-title">Find out which topics your target audience is mostly interested in</div>
              </div>
              <SingleSelectToggle
                className="sort-toggle"
                values={SORT_TYPES}
                selectedValue={sortType}
                onToggle={handleSortTypeChange}
                disabled={viewType === VIEW_TYPES_NAMES.table || (!interests && !sortedTopicsData)}
              />
            </div>
            <div className="topics-wrapper">
              <TopicSelector topics={sortedTopicsData} onSelect={handleTopicChange} selectedId={topic?.id} />
            </div>
          </>
        )}
        <div className="data-box-wrapper">
          <div
            className="data-box-top-toolbar"
            style={{ left: leftPosition, width: `calc(100% - ${leftPosition} - 20px)` }}
          >
            <div className="left sub-container">
              {(title === ALL_INTERESTS || title === ALL_CATEGORIES) &&
                currentViewType === VIEW_TYPES_NAMES.bars &&
                interests && (
                  <InterestInputBar
                    interestType={interestType}
                    value={newlyAddedInterestsToTest[interestType] || []}
                    onChange={(value) => setNewlyAddedInterestsToTest((prev) => ({ ...prev, [interestType]: value }))}
                    termsMold={termsMold}
                    keywordSuggester={keywordSuggester}
                    queryDataSelected={queryData.selected}
                    interests={interestType === 'categories' ? fullInterests : interests}
                    onDefineInterest={handleDefineInterest}
                  />
                )}
            </div>
            <div className="placeholder"></div>
            <div className="right sub-container">
              {!isCategoriesMode && (
                <div className="interest-type-toggle">
                  <SingleSelectToggle
                    values={interestTypes}
                    selectedValue={interestType}
                    onToggle={handleInterestTypeChange}
                  />
                </div>
              )}
              {!isCategoriesMode && (
                <div className="view-type-toggle">
                  <SingleSelectToggle
                    values={VIEW_TYPES}
                    selectedValue={currentViewType}
                    onToggle={handleViewTypeChange}
                    disabled={title !== ALL_INTERESTS && title !== ALL_CATEGORIES}
                  />
                </div>
              )}
            </div>
          </div>
          <div style={isCategoriesMode ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}>
            <SelectableBarChart
              data={!isCategoriesMode && currentViewType === VIEW_TYPES_NAMES.bars ? interests : []}
              chartTitle={title}
              noDataMsg={noDataMsg}
              showSpinner={!isCategoriesMode && (!interests || (interestType === 'websites' && !sortedTopicsData))}
              className={interestType}
              deleteFn={handleDeleteTestedInterest}
              query={queryData}
            />
          </div>
          {renderTable()}
        </div>
      </div>

      <AudienceInsightsBottomBar
        showTargets={showTargets}
        targets={targets}
        onOpenTargets={handleOpenTargets}
        isActivateEnabled={activation.isActivateEnabled}
        activation={activation}
        audienceId={activation.audienceId}
        audienceName={activation.audienceName}
        segment={segment}
        audienceChannel={audienceChannel}
        notificator={notificator}
        dspService={dspService}
        ssoService={ssoService}
        handleActivatingAudience={handleActivatingAudience}
        handleActivatingTarget={handleActivatingTarget}
        selectedCategory={selectedCategory}
        onActivateSelectedCategory={handleActivateSelectedCategory}
        isCategoryTargetingVisible={
          isCategoryTargetingVisible(abiPermissions) && isCategoriesMode && currentViewType === VIEW_TYPES_NAMES.table
        }
      />

      <ConfirmationModal
        isOpen={navBackModal.isOpen}
        modalTitle={navBackModal.title}
        message={navBackModal.message}
        onCancel={navToPrevTab}
        onSubmit={navToAudienceBuilder}
        submitLabel="Refine Audience"
        cancelLabel="Back"
      />
      <ComplianceModal
        isOpen={openComplianceModal}
        phrasesByTab={$scope?.nonCompliantPhrases || {}}
        text={$scope?.nonCompliantText || ''}
        onCloseCompliance={handleCloseCompliance}
        saveWithNonCompliant={handleSaveWithNonCompliant}
        saveWithoutNonCompliant={handleSaveWithoutNonCompliant}
      />

      {/* Category Activation Modals */}
      <SegmentNameModal
        isOpen={isSegmentNameModalOpen}
        initialName=""
        onCancel={handleSegmentNameCancel}
        onSubmit={handleSegmentNameSubmit}
      />
      {isCategoryActivationModalOpen && (
        <CategoryActivationModal
          isOpen={isCategoryActivationModalOpen}
          segmentName={pendingSegmentName}
          category={pendingCategory}
          marketsAndAdvertisersPromise={activation.marketsAndAdvertisersPromise}
          dspService={dspService}
          onCancel={handleCategoryActivationCancel}
          onSubmit={handleCategoryActivationSubmit}
          isProcessing={isCategoryActivationProcessing}
        />
      )}
    </div>
  );
};

AudienceInsightsWidget.propTypes = {
  handleActivatingAudience: PropTypes.func,
  handleActivatingTarget: PropTypes.func,
  navToPrevTab: PropTypes.func,
  navToAudienceBuilder: PropTypes.func,
  exportControl: PropTypes.object,
  selectedTab: PropTypes.object,
  tabsNames: PropTypes.object,
  audienceChannel: PropTypes.string,
  segment: PropTypes.array,
  context: PropTypes.object.isRequired,
  abiPermissions: PropTypes.object.isRequired,
  audienceTableStructure: PropTypes.object.isRequired,
  infraUtil: PropTypes.object.isRequired,
  termsMold: PropTypes.object.isRequired,
  keywordSuggester: PropTypes.object,
  defineInterestModal: PropTypes.object,
  notificator: PropTypes.object.isRequired,
  dspService: PropTypes.object.isRequired,
  ssoService: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  targetsCommon: PropTypes.object,
  targets: PropTypes.array,
  $state: PropTypes.object,
  $scope: PropTypes.object,
};

export default AudienceInsightsWidget;
